import React, { useEffect, useRef } from 'react'
import Editor from '@monaco-editor/react'

const LANGUAGE_ID = 'workflowDSL'

const DSLMonacoEditor = ({ value, onChange, variables = [], functions = [], routes = [] }) => {
  const monacoRef = useRef(null)
  const runtimeRef = useRef({
    variables: [],
    functions: [],
    routes: [],
  })

  // =====================================================
  // INITIAL SETUP
  // =====================================================

  const handleEditorWillMount = (monaco) => {
    // --------------------------------------------
    // REGISTER LANGUAGE
    // --------------------------------------------

    monaco.languages.register({
      id: LANGUAGE_ID,
    })

    // --------------------------------------------
    // TOKENIZER
    // --------------------------------------------

    monaco.languages.setMonarchTokensProvider(LANGUAGE_ID, {
      keywords: ['IF', 'THEN', 'ELSE', 'AND', 'OR'],

      tokenizer: {
        root: [
          [
            /[a-zA-Z_][\w]*/,
            {
              cases: {
                '@keywords': 'keyword',
                '@default': 'identifier',
              },
            },
          ],

          [/"[^"]*"/, 'string'],

          [/\d+/, 'number'],

          [/[><=!]+/, 'operator'],
        ],
      },
    })

    // --------------------------------------------
    // THEME
    // --------------------------------------------

    monaco.editor.defineTheme('workflowTheme', {
      base: 'vs-dark',
      inherit: true,

      rules: [
        {
          token: 'keyword',
          foreground: 'C586C0',
        },
        {
          token: 'number',
          foreground: 'B5CEA8',
        },
        {
          token: 'string',
          foreground: 'CE9178',
        },
        {
          token: 'operator',
          foreground: 'D4D4D4',
        },
        {
          token: 'identifier',
          foreground: '4FC1FF',
        },
      ],

      colors: {
        'editor.background': '#0f172a',
      },
    })
  }

  // =====================================================
  // DYNAMIC RUNTIME CONFIG
  // =====================================================

  useEffect(() => {
    runtimeRef.current = {
      variables,
      functions,
      routes,
    }
  }, [variables, functions, routes])

  const handleEditorDidMount = (editor, monaco) => {
    monacoRef.current = monaco

    // ============================================
    // COMPLETION PROVIDER
    // ============================================

    monaco.languages.registerCompletionItemProvider(LANGUAGE_ID, {
      triggerCharacters: ['.', '('],

      provideCompletionItems: () => {
        const suggestions = []

        const { variables, functions, routes } = runtimeRef.current

        // VARIABLES

        variables.forEach((variable) => {
          suggestions.push({
            label: variable,

            kind: monaco.languages.CompletionItemKind.Variable,

            insertText: variable,

            detail: 'Variable',
          })
        })

        // FUNCTIONS

        functions.forEach((fn) => {
          suggestions.push({
            label: fn.name,

            kind: monaco.languages.CompletionItemKind.Function,

            insertText: `${fn.name}($1)`,

            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,

            detail: 'Function',

            documentation: {
              value: `
${fn.docs || 'No documentation'}

${
  fn.example
    ? `
### Example

\`\`\`
${fn.example.trim()}
\`\`\`
`
    : ''
}
              `,
            },
          })
        })

        // ROUTES

        routes.forEach((route) => {
          suggestions.push({
            label: route,

            kind: monaco.languages.CompletionItemKind.Enum,

            insertText: `"${route}"`,

            detail: 'Vote Route',
          })
        })

        // KEYWORDS
        ;['IF', 'THEN', 'ELSE', 'AND', 'OR'].forEach((keyword) => {
          suggestions.push({
            label: keyword,

            kind: monaco.languages.CompletionItemKind.Keyword,

            insertText: keyword,
          })
        })

        return { suggestions }
      },
    })

    // ============================================
    // HOVER PROVIDER
    // ============================================

    monaco.languages.registerHoverProvider(LANGUAGE_ID, {
      provideHover: (model, position) => {
        const word = model.getWordAtPosition(position)

        if (!word) return

        const { functions } = runtimeRef.current

        const fn = functions.find((f) => f.name === word.word)

        if (!fn) return

        return {
          contents: [
            {
              value: `## ${fn.name}`,
            },

            {
              value: fn.docs || '_No description_',
            },

            fn.example && {
              value: `
### Example

\`\`\`
${fn.example.trim()}
\`\`\`
              `,
            },
          ].filter(Boolean),
        }
      },
    })
  }

  return (
    <Editor
      height="100%"
      language={LANGUAGE_ID}
      theme="workflowTheme"
      value={value}
      beforeMount={handleEditorWillMount}
      onMount={handleEditorDidMount}
      onChange={(val) => onChange(val || '')}
      options={{
        minimap: {
          enabled: false,
        },

        fontSize: 14,

        automaticLayout: true,

        scrollBeyondLastLine: false,

        padding: {
          top: 12,
        },

        suggestOnTriggerCharacters: true,

        quickSuggestions: true,

        wordBasedSuggestions: 'off',

        tabSize: 2,
      }}
    />
  )
}

export default DSLMonacoEditor
